import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { getAllCategoryAccounts } from '../components/SocialMedia/Home/HomeApis';
import validateSession, { initializeSession } from '../components/SocialMedia/Home/SessionApis';
import { chatInfo } from '../interfaces/chat';
import { ConfigurationInfo, accountInfo, appState } from '../interfaces/common';
import {
  CURRENT_TAB,
  RESET_BANNER,
  RESET_CHAT_INFO,
  SET_CHAT_INFO,
  SET_ENCRYPTION_FLAG,
  UPDATE_ACCOUNTS,
  UPDATE_BANNER,
  UPDATE_LOADER,
  UPDATE_SESSION,
  UPDATE_CUSTOM_TRANSLATION,
  UPDATE_ERROR_LOGS,
  UPDATE_ERROR_WEBHOOKS,
  UPDATE_CONFIGURATIONS,
} from './types';
import { fetchInboundErrors, updateErrorWebhooks } from '../components/monitor/MonitorApis';
import { getAllConfigurationRecords } from '../components/configure/LanguageService/LanguageServiceApis';
export const updateSessionDetails = (
  verified: boolean,
  valid: boolean,
  chatEnabled: boolean,
  sessionId: string,
  languages?: string[],
  platformVersion?: string,
  isClientPublickeyPresent?: boolean,
  enableAgentTrainer?: boolean,
  genAiKeyOption?: string,
  consumerId?: string,
  managerId?: string,
  errorWebhooks?: string[],
) => {
  return {
    type: UPDATE_SESSION,
    payload: {
      verified,
      valid,
      chatEnabled,
      sessionId,
      languages,
      platformVersion,
      isClientPublickeyPresent,
      enableAgentTrainer,
      genAiKeyOption,
      consumerId,
      managerId,
      errorWebhooks,
    },
  };
};

export const updateLoaderDetails = (isLoading: boolean) => {
  return {
    type: UPDATE_LOADER,
    payload: isLoading,
  };
};

export const updateCustomTranslationLanguage = (customizedLanguageSet: any) => {
  return {
    type: UPDATE_CUSTOM_TRANSLATION,
    payload: customizedLanguageSet,
  };
};

export const updateAccounts = (accountsData: accountInfo[] = []) => {
  return {
    type: UPDATE_ACCOUNTS,
    payload: accountsData,
  };
};

export const updateErrorLogs = (logData: any) => {
  return {
    type: UPDATE_ERROR_LOGS,
    payload: logData,
  };
};

export const updateErrWebhooks = (errorWebhooks: any) => {
  return {
    type: UPDATE_ERROR_WEBHOOKS,
    payload: errorWebhooks,
  };
};

export const setChatInfo = (WidgetInfo: chatInfo) => {
  return {
    type: SET_CHAT_INFO,
    payload: WidgetInfo,
  };
};

export const resetChatInfo = () => {
  return {
    type: RESET_CHAT_INFO,
  };
};

export const updateConfigRecords = (configRecords: ConfigurationInfo[] = []) => {
  return {
    type: UPDATE_CONFIGURATIONS,
    payload: configRecords,
  };
};

export const showBanner = (message: string, variant: string, heading?: string, forMonitorTab?: boolean) => {
  return {
    type: UPDATE_BANNER,
    payload: {
      message: message,
      variant: variant,
      heading: heading,
      forMonitorTab: forMonitorTab,
    },
  };
};

export const updateEncryptionFlag = (encryptionEnableFlag: boolean) => {
  return {
    type: SET_ENCRYPTION_FLAG,
    payload: { encryptionEnableFlag },
  };
};

export const updateCurrentTab = (tabName: string) => {
  return {
    type: CURRENT_TAB,
    payload: { tabName },
  };
};

export const updateBannerInfo =
  (
    message: string = '',
    variant: string,
    heading: string = '',
    forMonitorTab: boolean = false,
  ): ThunkAction<void, appState, unknown, Action<string>> =>
  async (dispatch) => {
    if (!message) return;
    dispatch(showBanner(message, variant, heading, forMonitorTab));
    if (message.indexOf('Invalid or expired session') !== -1) {
      dispatch(updateSessionDetails(true, false, false, ''));
    }
  };

export const resetBannerInfo = () => {
  return {
    type: RESET_BANNER,
  };
};

export const fetchAllAccounts =
  (): ThunkAction<void, appState, unknown, Action<string>> => async (dispatch, getState) => {
    const sessionID = getState().sessionId;
    dispatch(updateLoaderDetails(true));
    const allAccountsRep = await getAllCategoryAccounts(sessionID);
    dispatch(updateLoaderDetails(false));
    if (allAccountsRep?.status === 200) {
      dispatch(updateAccounts(allAccountsRep.data));
    } else {
      dispatch(updateAccounts([]));
      dispatch(updateBannerInfo(allAccountsRep?.errorMessage, 'urgent'));
    }
  };

export const fetchErrorLogs =
  (payload: any): ThunkAction<void, appState, unknown, Action<string>> =>
  async (dispatch, getState) => {
    const sessionID = getState().sessionId;
    dispatch(updateLoaderDetails(true));
    const allErrorLogs = await fetchInboundErrors(sessionID, payload);
    if (allErrorLogs?.status === 200 && allErrorLogs.data.length === 0) {
      if (allErrorLogs.nextToken) {
        return dispatch(fetchErrorLogs({ ...payload, nextToken: allErrorLogs.nextToken }));
      }
    }
    if (allErrorLogs?.status === 200) {
      dispatch(updateErrorLogs(allErrorLogs));
    } else {
      dispatch(updateErrorLogs([]));
      dispatch(updateBannerInfo(allErrorLogs?.errorMessage, 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
  };

export const updateErrorWebhook =
  (webhooks: string[]): ThunkAction<void, appState, unknown, Action<string>> =>
  async (dispatch, getState) => {
    const sessionId = getState().sessionId;
    dispatch(updateLoaderDetails(true));
    const response = await updateErrorWebhooks(sessionId, webhooks);
    if (response?.status === 200) {
      dispatch(updateErrWebhooks(response));
    } else {
      dispatch(updateErrWebhooks([]));
      dispatch(updateBannerInfo(response?.errorMessage, 'urgent'));
    }
    dispatch(updateLoaderDetails(false));
  };

export const fetchSession =
  (sessionID: string | undefined): ThunkAction<void, appState, unknown, Action<string>> =>
  async (dispatch) => {
    sessionID = sessionID ? sessionID : '';
    if (sessionID) {
      dispatch(updateLoaderDetails(true));
      const session: any = await initializeSession(sessionID);
      dispatch(updateLoaderDetails(false));
      if (!session || session?.errorMessage) {
        dispatch(updateSessionDetails(true, false, false, ''));
        dispatch(
          updateBannerInfo(session?.errorMessage ? session?.errorMessage : 'Invalid or expired session', 'urgent'),
        );
      } else {
        sessionStorage.setItem('sessionId', session.sessionId);
        dispatch(
          updateSessionDetails(
            true,
            true,
            session.c,
            session.sessionId,
            session?.languages,
            session?.platformVersion,
            session?.isClientPublicKeyPresent,
            session?.enableAgentTrainer,
            session?.genAiKeyOption,
            session?.consumerId,
            session?.managerId,
            session?.errorWebhooks,
          ),
        );
      }
    } else {
      let sessionId = sessionStorage.getItem('sessionId');
      sessionId = sessionId || '';
      dispatch(updateLoaderDetails(true));
      const result = await validateSession(sessionId);
      dispatch(updateLoaderDetails(false));
      if (!result || result?.errorMessage) {
        dispatch(updateSessionDetails(true, false, false, sessionId));
        dispatch(
          updateBannerInfo(result?.errorMessage ? result?.errorMessage : 'Invalid or expired session', 'urgent'),
        );
      } else {
        dispatch(
          updateSessionDetails(
            true,
            true,
            result.c,
            sessionId,
            result?.languages,
            result?.platform_version,
            result?.is_client_publickey_present,
            result?.enable_agent_trainer,
            result?.genAi_key_option,
            result?.consumer_id,
            result?.manager_id,
            result?.error_webhooks,
          ),
        );
      }
    }
  };

export const fetchAllConfigurationRecords =
  (): ThunkAction<void, appState, unknown, Action<string>> => async (dispatch, getState) => {
    const sessionID = getState().sessionId;
    dispatch(updateLoaderDetails(true));
    const allConfigsRep = await getAllConfigurationRecords(sessionID);
    dispatch(updateLoaderDetails(false));
    if (allConfigsRep?.status === 200) {
      const updatedData: ConfigurationInfo[] = Object.values(allConfigsRep.data);
      dispatch(updateConfigRecords(updatedData));
    } else {
      dispatch(updateConfigRecords([]));
      dispatch(updateBannerInfo(allConfigsRep?.errorMessage, 'urgent'));
    }
  };
